import React, { useMemo, useState } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Button,
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
  Modal,
  ModalHeader,
  ModalBody,
  Badge,
} from "reactstrap";
import { toast, ToastContainer } from "react-toastify";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import TableContainer from "../../../Components/Common/TableContainer";
import Loader from "../../../Components/Common/Loader";
import DocumentChecklistForm from "./DocumentChecklistForm";
import {
  useGetDocumentChecklistQuery,
  useDeleteDocumentChecklistMutation,
} from "slices/InventoryManagement/inventoryManagementSlice";
import { IsValidPermissions } from "Routes/AuthProtected";

interface DocumentChecklistItem {
  id: number;
  document_name: string;
  description?: string | null;
  file_url?: string | null;
  // is_required: boolean;
  status: string;
  created_at?: string;
  modified_at?: string | null;
}

interface DocumentChecklistResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: DocumentChecklistItem[];
}

const DocumentChecklistList: React.FC = () => {
  const [showModal, setShowModal] = useState(false);
  const [editId, setEditId] = useState<number | null>(null);
  const [viewId, setViewId] = useState<number | null>(null);
  const [deleteId, setDeleteId] = useState<number | null>(null);

  const { data, isLoading, error, refetch } = useGetDocumentChecklistQuery({
    page: 1,
    page_size: 100,
  });
  const [deleteDoc, { isLoading: deleting }] =
    useDeleteDocumentChecklistMutation();

  const list: DocumentChecklistItem[] = (data as DocumentChecklistResponse)
    ?.results ?? [];

  const openAdd = () => {
    setEditId(null);
    setShowModal(true);
  };
  const openEdit = (id: number) => {
    setEditId(id);
    setShowModal(true);
  };
  const closeModal = () => {
    setShowModal(false);
    setEditId(null);
    refetch();
  };
  const openView = (id: number) => setViewId(id);
  const closeView = () => setViewId(null);

  const handleDelete = async () => {
    if (deleteId == null) return;
    try {
      await deleteDoc(deleteId).unwrap();
      toast.success("Document checklist deleted");
      setDeleteId(null);
      refetch();
    } catch (e) {
      toast.error("Failed to delete");
    }
  };

  const columns = useMemo(
    () => [
      { Header: "ID", accessor: "id", width: 70 },
      {
        Header: "Document Name",
        accessor: "document_name",
        Cell: (cell: any) => cell.row.original.document_name,
      },
      // {
      //   Header: "Required",
      //   accessor: "is_required",
      //   Cell: (cell: any) =>
      //     cell.row.original.is_required ? (
      //       <Badge color="success">Yes</Badge>
      //     ) : (
      //       <Badge color="secondary">No</Badge>
      //     ),
      // },
      {
        Header: "Status",
        accessor: "status",
        Cell: (cell: any) => {
          const s = cell.row.original.status;
          if (s == null || s === undefined) return <Badge color="secondary">—</Badge>;
          const str = String(s);
          return (
            <Badge color={str === "active" ? "success" : "secondary"}>{str}</Badge>
          );
        },
      },
      {
        Header: "File",
        accessor: "file_url",
        Cell: (cell: any) =>
          cell.row.original.file_url ? (
            <a
              href={cell.row.original.file_url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary"
            >
              View
            </a>
          ) : (
            <span className="text-muted">—</span>
          ),
      },
      {
        Header: "Action",
        Cell: (cell: any) => {
          const row = cell.row.original as DocumentChecklistItem;
          return (
            <UncontrolledDropdown>
              <DropdownToggle
                className="btn btn-soft-secondary btn-sm"
                tag="button"
              >
                <i className="ri-more-fill" />
              </DropdownToggle>
              <DropdownMenu className="dropdown-menu-end">
                <DropdownItem
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    openView(row.id);
                  }}
                >
                  <i className="ri-eye-fill align-bottom me-2 text-muted" /> View
                </DropdownItem>
                {IsValidPermissions(["EDIT_INVENTORY_TAG"]) && (
                  <DropdownItem
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      openEdit(row.id);
                    }}
                  >
                    <i className="ri-pencil-fill align-bottom me-2 text-muted" />{" "}
                    Edit
                  </DropdownItem>
                )}
                {IsValidPermissions(["EDIT_INVENTORY_TAG"]) && (
                  <DropdownItem
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      setDeleteId(row.id);
                    }}
                  >
                    <i className="ri-delete-bin-fill align-bottom me-2 text-muted" />{" "}
                    Delete
                  </DropdownItem>
                )}
              </DropdownMenu>
            </UncontrolledDropdown>
          );
        },
      },
    ],
    []
  );

  const colsWithDisable = useMemo(
    () => columns.map((c) => ({ ...c, disableSortBy: true })),
    [columns]
  );

  document.title = "Document Checklist | Resource Inventory";

  if (isLoading) {
    return (
      <div className="page-content">
        <Container fluid>
          <BreadCrumb title="Document Checklist" pageTitle="Resource Inventory" />
          <div className="text-center py-5">
            <Loader />
          </div>
        </Container>
      </div>
    );
  }

  return (
    <div className="page-content">
      <ToastContainer closeButton={false} limit={1} />
      <Container fluid>
        <BreadCrumb
          title="Document Checklist"
          pageTitle="Resource Inventory"
        />

        <Row>
          <Col xl={12}>
            <Card>
              <div className="card-header border-0 d-flex align-items-center justify-content-between">
                <h5 className="mb-0">Document Checklist</h5>
                <div>
                  {(IsValidPermissions(["VIEW_ALL_INVENTORY_TAG"]) ||
                    IsValidPermissions(["VIEW_ONLY_INVENTORY_TAG"])) &&
                    IsValidPermissions(["ADD_INVENTORY_TAG"]) && (
                    <Button color="primary" onClick={openAdd}>
                      Add Document Type
                    </Button>
                    )}
                </div>
              </div>

              <div className="card-body pt-0">
                {error && (
                  <div className="alert alert-danger">
                    Failed to load document checklist.
                  </div>
                )}
                {list.length > 0 ? (
                  <TableContainer
                    columns={colsWithDisable}
                    data={list}
                    isGlobalFilter
                    customPageSize={10}
                    divClass="table-responsive mb-1"
                    tableClass="mb-0 align-middle table-borderless"
                    theadClass="table-light text-muted"
                    SearchPlaceholder="Search document types..."
                    isPagination
                    showPageSummary={true}
                  />
                ) : (
                  <div className="py-4 text-center">
                    <i className="ri-file-list-3-line display-5 text-muted" />
                    <h5 className="mt-3">No document types yet</h5>
                    <p className="text-muted">
                      Add document types (e.g. COC, Invoice) that can be
                      mapped to products.
                    </p>
                    {IsValidPermissions(["ADD_INVENTORY_TAG"]) && (
                      <Button color="primary" onClick={openAdd}>
                        Add Document Type
                      </Button>
                    )}
                  </div>
                )}
              </div>
            </Card>
          </Col>
        </Row>

        <Modal isOpen={showModal} toggle={closeModal} size="lg">
          <ModalHeader toggle={closeModal}>
            {editId != null ? "Edit Document Type" : "Add Document Type"}
          </ModalHeader>
          <ModalBody>
            <DocumentChecklistForm
              id={editId ?? undefined}
              onSaved={closeModal}
            />
          </ModalBody>
        </Modal>

        <Modal isOpen={viewId != null} toggle={closeView} size="md">
          <ModalHeader toggle={closeView}>Document Type Details</ModalHeader>
          <ModalBody>
            {viewId != null && list.find((r) => r.id === viewId) && (
              <table className="table table-borderless mb-0">
                <tbody>
                  <tr>
                    <td className="fw-medium">Name</td>
                    <td>
                      {list.find((r) => r.id === viewId)?.document_name ?? "—"}
                    </td>
                  </tr>
                  <tr>
                    <td className="fw-medium">Description</td>
                    <td>
                      {list.find((r) => r.id === viewId)?.description ?? "—"}
                    </td>
                  </tr>
                  {/* <tr>
                    <td className="fw-medium">Required</td>
                    <td>
                      {list.find((r) => r.id === viewId)?.is_required
                        ? "Yes"
                        : "No"}
                    </td>
                  </tr> */}
                  <tr>
                    <td className="fw-medium">Status</td>
                    <td>
                      {list.find((r) => r.id === viewId)?.status ?? "—"}
                    </td>
                  </tr>
                  <tr>
                    <td className="fw-medium">File</td>
                    <td>
                      {list.find((r) => r.id === viewId)?.file_url ? (
                        <a
                          href={list.find((r) => r.id === viewId)!.file_url!}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          View file
                        </a>
                      ) : (
                        "—"
                      )}
                    </td>
                  </tr>
                </tbody>
              </table>
            )}
          </ModalBody>
        </Modal>

        <Modal
          isOpen={deleteId != null}
          toggle={() => setDeleteId(null)}
          centered
        >
          <ModalHeader toggle={() => setDeleteId(null)}>
            Delete Document Type
          </ModalHeader>
          <ModalBody>
            Are you sure you want to delete this document type? Products may
            reference it.
          </ModalBody>
          <div className="modal-footer">
            <Button color="secondary" onClick={() => setDeleteId(null)}>
              Cancel
            </Button>
            <Button
              color="danger"
              onClick={handleDelete}
              disabled={deleting}
            >
              {deleting ? "Deleting..." : "Delete"}
            </Button>
          </div>
        </Modal>
      </Container>
    </div>
  );
};

export default DocumentChecklistList;
